// ════════════════════════════════════════════════════════════
// CommandPalette — ⌘K, style Linear/Raycast (Sprint Shell)
// ════════════════════════════════════════════════════════════
// cmdk + Dialog (design system). Montée au root, ouverte via
// useCommandPaletteStore (raccourci ⌘/Ctrl+K géré dans AppShell).
// Sections : Navigation (filtrée rôle) · Récents (5) · Actions
// globales (+ commandes enregistrées via commandRegistry).
// ════════════════════════════════════════════════════════════
import { Command } from 'cmdk';
import { useNavigate, useLocation } from '@tanstack/react-router';
import { Search, Clock, Palette, PanelLeft, Settings, LogOut, type LucideIcon } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';
import type { Theme } from '@/store/useAppStore';
import { useShellStore } from '@/stores/useShellStore';
import { useCommandPaletteStore } from './command-palette-store';
import { useCommandRegistryStore } from './commandRegistry';
import { useVisibleNavItems } from './use-shell-nav';
import { buildPaletteSections, type ShellActionId } from './palette-commands';

const ACTION_ICONS: Record<ShellActionId, LucideIcon> = {
  'toggle-theme': Palette,
  'toggle-sidebar': PanelLeft,
  'go-account': Settings,
  logout: LogOut,
};

const THEME_CYCLE: readonly Theme[] = ['system', 'light', 'dark'] as const;

const itemClass = cn(
  'flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-sm outline-none',
  'text-foreground/85 aria-selected:bg-accent aria-selected:text-accent-foreground',
);

export function CommandPalette() {
  const open = useCommandPaletteStore((s) => s.open);
  const setOpen = useCommandPaletteStore((s) => s.setOpen);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { session, profile, signOut } = useAuth();
  const { theme, setTheme } = useTheme();
  const toggleSidebar = useShellStore((s) => s.toggleSidebar);
  const recents = useShellStore((s) => s.recentPages);
  const { visible, role } = useVisibleNavItems();
  const registered = useCommandRegistryStore((s) => s.commands);

  // Pas de palette tant qu'on n'est pas authentifié (montée au root).
  if (!session || !profile) return null;

  const {
    navigation,
    recents: recentPages,
    actions,
  } = buildPaletteSections({
    role,
    navItems: visible,
    recents,
    currentPath: pathname,
  });

  const visibleRegistered = registered.filter((c) => (c.visible ? c.visible(role) : true));

  const close = () => {
    setOpen(false);
  };

  const go = (to: string) => {
    close();
    void navigate({ to });
  };

  const runAction = (id: ShellActionId) => {
    switch (id) {
      case 'toggle-theme': {
        const next = THEME_CYCLE[(THEME_CYCLE.indexOf(theme) + 1) % THEME_CYCLE.length] ?? 'system';
        setTheme(next);
        close();
        break;
      }
      case 'toggle-sidebar':
        toggleSidebar();
        close();
        break;
      case 'go-account':
        go('/compte');
        break;
      case 'logout':
        close();
        void (async () => {
          await signOut();
          toast.success('Déconnecté');
          await navigate({ to: '/login' });
        })();
        break;
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen} mobileVariant="centered">
      <DialogContent
        size="lg"
        closeButton={false}
        className="overflow-hidden p-0"
        aria-describedby={undefined}
      >
        <DialogTitle className="sr-only">Palette de commandes</DialogTitle>
        <Command
          label="Palette de commandes"
          className="flex max-h-[60vh] flex-col"
          // Fuzzy par défaut sur le textContent ; on filtre aussi sur les keywords.
        >
          <div className="border-border flex items-center gap-2 border-b px-3">
            <Search className="text-muted-foreground size-4 shrink-0" />
            <Command.Input
              autoFocus
              placeholder="Rechercher une page, une action…"
              className="placeholder:text-muted-foreground h-12 w-full bg-transparent text-sm outline-none"
            />
          </div>

          <Command.List className="overflow-y-auto p-2">
            <Command.Empty className="text-muted-foreground py-8 text-center text-sm">
              Aucun résultat.
            </Command.Empty>

            {navigation.length > 0 && (
              <Command.Group
                heading="Navigation"
                className="text-muted-foreground/70 [&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-1 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider"
              >
                {navigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Command.Item
                      key={item.to}
                      value={`${item.label} ${item.to}`}
                      onSelect={() => {
                        go(item.to);
                      }}
                      className={itemClass}
                    >
                      <Icon className="text-muted-foreground size-4 shrink-0" />
                      {item.label}
                    </Command.Item>
                  );
                })}
              </Command.Group>
            )}

            {recentPages.length > 0 && (
              <Command.Group
                heading="Récents"
                className="text-muted-foreground/70 [&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider"
              >
                {recentPages.map((page) => (
                  <Command.Item
                    key={page.path}
                    value={`recent ${page.label} ${page.path}`}
                    onSelect={() => {
                      go(page.path);
                    }}
                    className={itemClass}
                  >
                    <Clock className="text-muted-foreground size-4 shrink-0" />
                    {page.label}
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            <Command.Group
              heading="Actions"
              className="text-muted-foreground/70 [&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider"
            >
              {actions.map((action) => {
                const Icon = ACTION_ICONS[action.id];
                return (
                  <Command.Item
                    key={action.id}
                    value={action.label}
                    onSelect={() => {
                      runAction(action.id);
                    }}
                    className={itemClass}
                  >
                    <Icon className="text-muted-foreground size-4 shrink-0" />
                    {action.label}
                  </Command.Item>
                );
              })}
            </Command.Group>

            {/* Commandes enregistrées par les modules (architecture prête). */}
            {visibleRegistered.length > 0 && (
              <Command.Group
                heading="Raccourcis"
                className="text-muted-foreground/70 [&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider"
              >
                {visibleRegistered.map((cmd) => {
                  const Icon = cmd.icon;
                  return (
                    <Command.Item
                      key={cmd.id}
                      value={`${cmd.label} ${(cmd.keywords ?? []).join(' ')}`}
                      onSelect={() => {
                        close();
                        cmd.run();
                      }}
                      className={itemClass}
                    >
                      {Icon && <Icon className="text-muted-foreground size-4 shrink-0" />}
                      {cmd.label}
                    </Command.Item>
                  );
                })}
              </Command.Group>
            )}
          </Command.List>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
